import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { AudioTrack } from '../types';
import { SoundtrackService } from '../../../services/soundtrackService';
import { uploadAudio, saveMergedAudio } from '../../../services/audioApi';
import { useNotification } from '../../../context/NotificationContext';
import { getApiUrl } from '../../../config/api';
import { useAuth } from '../../../context/Auth';
import { downloadVideo } from '../../../services/autoDownloadService';

interface SoundtrackExportButtonProps {
  projectId: string;
  tracks: AudioTrack[];
  masterVolume: number;
  generateMixedAudio: (tracksToMix: AudioTrack[]) => Promise<Blob>;
  videoUrl?: string; // NEW: Video URL for audio extraction
  onExportStart: () => void;
  onExportComplete: (jobId: string) => void;
  onExportError: (error: string) => void;
  disabled?: boolean;
}

export const SoundtrackExportButton: React.FC<SoundtrackExportButtonProps> = ({
  projectId,
  tracks,
  masterVolume,
  generateMixedAudio,
  videoUrl, // NEW: Add videoUrl
  onExportStart,
  onExportComplete,
  onExportError,
  disabled = false
}) => {
  const { t } = useTranslation('soundGenerator');
  const [isExporting, setIsExporting] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobProgress, setJobProgress] = useState<number>(0);
  const [isProcessingJob, setIsProcessingJob] = useState(false);
  const [hasOpenedVideo, setHasOpenedVideo] = useState(false); // Prevent double opening
  const { showNotification } = useNotification();
  const { token } = useAuth(); // Get auth token for proxy requests
  const soundtrackService = new SoundtrackService();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Start job status polling
  const startJobPolling = (jobId: string) => {
    setJobId(jobId);
    setIsProcessingJob(true);
    setJobProgress(0);
    setHasOpenedVideo(false); // Reset flag when starting new job

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const status = await soundtrackService.getSoundtrackStatus(projectId);
        
        if (status.jobStatus) {
          const percentage = status.jobStatus.percentage || 0;
          setJobProgress(percentage);

          if (status.jobStatus.status === 'completed') {
            // Job completed successfully
            setIsProcessingJob(false);
            setJobId(null);
            setJobProgress(0);
            
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
            }

            // Auto-download video only once
            if (status.soundtrackVideoUrl && !hasOpenedVideo) {
              console.log(t('soundtrackExport.console.autoDownloading'));
              setHasOpenedVideo(true);
              
              // Download the video using proxy
              setTimeout(async () => {
                try {
                  await downloadVideo(status.soundtrackVideoUrl!, {
                    filename: `soundtrack-video-${Date.now()}.mp4`,
                    onStart: () => {
                      console.log(t('soundtrackExport.console.downloadStarted'));
                    },
                    onComplete: () => {
                      console.log(t('soundtrackExport.console.downloadSuccess'));
                      showNotification({
                        title: t('soundtrackExport.notifications.videoReadyTitle'),
                        message: t('soundtrackExport.notifications.videoReadyMessage'),
                        type: 'success'
                      });
                    },
                    onError: (error) => {
                      console.error(t('soundtrackExport.console.downloadFailed'), error);
                      showNotification({
                        title: t('soundtrackExport.notifications.downloadFailedTitle'),
                        message: t('soundtrackExport.notifications.downloadFailedMessage'),
                        type: 'warning'
                      });
                    }
                  });
                } catch (error) {
                  console.error(t('soundtrackExport.console.downloadFailed'), error);
                }
              }, 1000);
            }
          } else if (status.jobStatus.status === 'failed') {
            // Job failed
            setIsProcessingJob(false);
            setJobId(null);
            setJobProgress(0);
            
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
            }

            showNotification({
              title: t('soundtrackExport.notifications.exportFailedTitle'),
              message: t('soundtrackExport.notifications.exportFailedMessage'),
              type: 'error'
            });
          }
        }
      } catch (error) {
        console.error('Error polling job status:', error);
        // Continue polling on error - don't stop unless max retries reached
      }
    }, 3000); // Poll every 3 seconds
  };

  // Get button text based on current state
  const getButtonText = () => {
    if (isExporting) {
      return currentStep || t('soundtrackExport.buttons.processing');
    } else if (isProcessingJob && jobProgress > 0) {
      return t('soundtrackExport.buttons.exportVideoProgress', { progress: jobProgress });
    } else if (isProcessingJob) {
      return t('soundtrackExport.buttons.exportVideoProgress', { progress: 0 });
    } else {
      return t('soundtrackExport.buttons.exportVideo');
    }
  };

  // Check if button should be disabled
  const isButtonDisabled = disabled || tracks.length === 0 || isExporting || isProcessingJob;

  // Extract audio from video file using proxy
  const extractVideoAudio = async (videoUrl: string): Promise<AudioBuffer | null> => {
    try {
      console.log(t('soundtrackExport.console.videoDetected'), videoUrl);
      setCurrentStep(t('soundtrackExport.steps.extractingAudio'));
      // Get authentication token for backend request
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {};
    
       // Add authorization header if token exists
       if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

       const response = await fetch(getApiUrl(`/api/proxy-video?url=${encodeURIComponent(videoUrl)}`), {
                method: 'GET',
                headers
              });


      if (!response.ok) {
        throw new Error(`Failed to fetch video: ${response.status}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      console.log('✅ Video audio extracted successfully, duration:', audioBuffer.duration);
      await audioContext.close();
      return audioBuffer;
    } catch (error) {
      console.error('❌ Failed to extract video audio:', error);
      return null;
    }
  };

  // Create a virtual audio track for the video audio
  const createVideoAudioTrack = (audioBuffer: AudioBuffer): AudioTrack => {
    return {
      id: `video-audio-${Date.now()}`,
      name: 'Video Audio',
      url: '',
      audioBuffer: audioBuffer,
      startTime: 0,
      duration: audioBuffer.duration,
      originalDuration: audioBuffer.duration,
      volume: 1.0,
      isMuted: false,
      color: '#ff6b6b',
      laneIndex: 0,
      fadeInDuration: 0,
      fadeOutDuration: 0,
      trimStart: 0,
      trimEnd: 0,
      isSelected: false,
      isVideo: true,
      isLocked: true,
      isSolo: false,
      pan: 0
    };
  };

  const handleExport = async () => {
    if (!projectId || tracks.length === 0) {
      showNotification({
        title: t('soundtrackExport.notifications.noTracksTitle'),
        message: t('soundtrackExport.notifications.noTracksMessage'),
        type: 'error'
      });
      return;
    }

    setIsExporting(true);
    onExportStart();

    try {
      // STEP 0: Extract video audio if video URL exists
      let tracksToMix = [...tracks];
      
      if (videoUrl) {
        console.log(t('soundtrackExport.console.videoDetected'), videoUrl);
        const videoAudioBuffer = await extractVideoAudio(videoUrl);
        
        if (videoAudioBuffer) {
          console.log(t('soundtrackExport.console.creatingVideoTrack'));
          const videoTrack = createVideoAudioTrack(videoAudioBuffer);
          tracksToMix = [videoTrack, ...tracks];
          console.log(t('soundtrackExport.console.mixingTracks', { 
            count: tracksToMix.length, 
            timeline: tracks.length 
          }));
        } else {
          console.warn(t('soundtrackExport.console.videoExtractionFailed'));
        }
      } else {
        console.log(t('soundtrackExport.console.noVideoUrl'));
      }

      // Step 1: Generate mixed audio (now includes video audio if available)
      setCurrentStep(t('soundtrackExport.steps.mixingTracks'));
      const mixedAudioBlob = await generateMixedAudio(tracksToMix);

      // Step 2: Save merged audio to existing project
      setCurrentStep(t('soundtrackExport.steps.uploadingAudio'));
      const uploadResponse = await saveMergedAudio(mixedAudioBlob, projectId, tracksToMix);
      
      console.log('📦 Upload response:', uploadResponse);
      console.log('🔗 Asset URL:', uploadResponse.data?.assetUrl);

      // Step 3: Create video with soundtrack
      setCurrentStep(t('soundtrackExport.steps.creatingVideo'));
      
      const soundtrackData = soundtrackService.prepareSoundtrackData(
        projectId,
        uploadResponse.data.s3Url,
        tracksToMix,
        masterVolume
      );
      
      console.log('🎯 Final payload being sent:', JSON.stringify(soundtrackData, null, 2));

      const exportResponse = await soundtrackService.exportSoundtrackVideo(soundtrackData);

      if (exportResponse.success && exportResponse.jobId) {
        startJobPolling(exportResponse.jobId);
        onExportComplete(exportResponse.jobId);
      } else {
        throw new Error('Failed to start soundtrack export');
      }

    } catch (error) {
      console.error('Error exporting soundtrack:', error);
      const errorMessage = error instanceof Error ? error.message : t('soundtrackExport.notifications.errorMessage');
      
      showNotification({
        title: t('soundtrackExport.notifications.errorTitle'),
        message: errorMessage,
        type: 'error'
      });

      onExportError(errorMessage);
    } finally {
      setIsExporting(false);
      setCurrentStep('');
    }
  };

  return (
    <Button
      onClick={handleExport}
      disabled={isButtonDisabled}
      style={{
        cursor: isButtonDisabled ? "not-allowed !important" : "pointer",
        background: isButtonDisabled 
          ?  "#a855f7" 
          : "linear-gradient(135deg, #8b5cf6, #a855f7)",
        border: "none",
        color: isButtonDisabled ? "#d1d5db !important" : "white",
        borderRadius: "8px",
        padding: "8px 16px",
        fontWeight: 500,
        transition: "all 0.3s ease",
        boxShadow: isButtonDisabled 
          ? "none !important" 
          : "0 2px 8px 0 rgba(139, 92, 246, 0.3)",
        marginLeft: "12px",
        opacity: isButtonDisabled ? "0.6 !important" : "1"
      }}
      onMouseEnter={(e) => {
        if (!isButtonDisabled) {
          e.currentTarget.style.transform = "translateY(-1px)";
          e.currentTarget.style.boxShadow = "0 4px 12px 0 rgba(139, 92, 246, 0.4)";
        }
      }}
      onMouseLeave={(e) => {
        if (!isButtonDisabled) {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 2px 8px 0 rgba(139, 92, 246, 0.3)";
        }
      }}
    >
      <FontAwesomeIcon 
        icon={(isExporting || isProcessingJob) ? faSpinner : faVideo} 
        style={{ marginRight: "8px" }} 
        spin={isExporting || isProcessingJob}
      />
      {getButtonText()}
    </Button>
  );
};
